import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCaptureModal } from '@/contexts/CaptureModalContext';
import { actionCardsApi, foldersApi, teamsApi, organizationApi } from '@/utils/api';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { relTime } from '@/utils/time';
import { TagSearchInput } from '@/components/TagSearchInput';
import { CardsSideNav } from '@/components/CardsSideNav';
import {
  Plus, Zap, Users, Phone, Mail, FileText, PenLine, Sparkles, MoreHorizontal,
  ExternalLink, FolderInput, Trash2, X, Check, SlidersHorizontal, ArrowUpDown,
  Settings2, Folder, FolderPlus, Inbox, ChevronDown, ChevronRight, Pin, Building2, ListChecks,
} from 'lucide-react';

// Map a card's `source` to a v2 conversation-type icon + accent.
const TYPE_META = {
  meeting: { cls: 'meeting', Icon: Users, tip: 'Meeting' },
  audio: { cls: 'call', Icon: Phone, tip: 'Recording' },
  follow_up: { cls: 'email', Icon: Mail, tip: 'Follow-up' },
  text: { cls: 'email', Icon: Mail, tip: 'Pasted transcript' },
  quick_note: { cls: 'draft', Icon: FileText, tip: 'Quick note' },
  manual: { cls: 'draft', Icon: PenLine, tip: 'Manual entry' },
};
const typeMetaOf = (src) => TYPE_META[src] || TYPE_META.meeting;
const folderColorOf = (f) => (f && f.color) || '#b08900';

export default function ActionCardsPage() {
  const { openModal } = useCaptureModal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scopeParam = searchParams.get('scope'); // null | 'personal' | 'team'

  const [cards, setCards] = useState([]);
  const [folders, setFolders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [orgTeams, setOrgTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  // Rail / folder-tree state
  const [activeFolder, setActiveFolder] = useState(scopeParam === 'personal' || scopeParam === 'team' ? scopeParam : 'all'); // 'all' | 'personal' | 'team' | folderId
  const [expanded, setExpanded] = useState({});
  const [expandedWs, setExpandedWs] = useState({});
  const [creatingFor, setCreatingFor] = useState(null); // null | 'personal' | teamId
  const [newName, setNewName] = useState('');
  const [showAllWsFolders, setShowAllWsFolders] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dropTarget, setDropTarget] = useState(null);

  useEffect(() => {
    setActiveFolder(scopeParam === 'personal' || scopeParam === 'team' ? scopeParam : 'all');
  }, [scopeParam]);

  // Toolbar / filter state
  const [openMenu, setOpenMenu] = useState(null); // 'filter' | 'sort' | 'cust'
  const [rowMenuId, setRowMenuId] = useState(null);
  const [openTasksOnly, setOpenTasksOnly] = useState(false);
  const [wsFilter, setWsFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgTeamFilter, setOrgTeamFilter] = useState('');
  const [sortBy, setSortBy] = useState('new'); // 'new' | 'tasks'
  const [hiddenCols, setHiddenCols] = useState({ tk: false, when: false });
  const [collapsed, setCollapsed] = useState({});

  // Modals
  const [moveTarget, setMoveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [folderConfirmName, setFolderConfirmName] = useState('');
  const [deletingFolder, setDeletingFolder] = useState(false);

  // Column visibility → toggle classes on the app root (v2 .mlk-app.hide-* selectors).
  useEffect(() => {
    const root = document.querySelector('.mlk-app');
    if (!root) return;
    ['tk', 'when'].forEach(k => root.classList.toggle(`hide-${k}`, !!hiddenCols[k]));
    return () => { ['tk', 'when'].forEach(k => root.classList.remove(`hide-${k}`)); };
  }, [hiddenCols]);

  // Close any open toolbar/row menu on outside click / Escape.
  useEffect(() => {
    if (!openMenu && !rowMenuId) return;
    const onDown = (e) => { if (!e.target.closest('[data-menuroot]')) { setOpenMenu(null); setRowMenuId(null); } };
    const onKey = (e) => { if (e.key === 'Escape') { setOpenMenu(null); setRowMenuId(null); setMoveTarget(null); setDeleteTarget(null); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [openMenu, rowMenuId]);

  useEffect(() => {
    organizationApi.get().then(res => {
      if (res.data.company) organizationApi.teams().then(t => setOrgTeams(t.data.teams || [])).catch(() => {});
    }).catch(() => {});
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const [fr, tr] = await Promise.all([foldersApi.list(), teamsApi.list()]);
      setFolders(fr.data.folders || []);
      setTeams(tr.data.teams || []);
    } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (search) params.search = search;
      if (activeFolder === 'personal' || activeFolder === 'team') params.scope = activeFolder;
      else if (activeFolder !== 'all') params.folder_id = activeFolder;
      if (orgTeamFilter) params.org_team_id = orgTeamFilter;
      const res = await actionCardsApi.list(params);
      setCards(res.data.action_cards || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load Action Cards'); }
    finally { setLoading(false); }
  }, [search, activeFolder, orgTeamFilter]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  // ── Folder tree helpers ───────────────────────────────────────────
  const topTeam = folders.filter(f => f.scope === 'team' && !f.parent_id);
  const subOf = (id) => folders.filter(f => f.parent_id === id);
  const teamFoldersOf = (tid) => folders.filter(f => f.scope === 'team' && f.team_id === tid);

  const createFolder = async () => {
    const name = newName.trim();
    const forVal = creatingFor;
    if (!name) { setCreatingFor(null); return; }
    try {
      await foldersApi.create({
        name,
        scope: forVal === 'personal' ? 'personal' : 'team',
        team_id: forVal === 'personal' ? undefined : forVal,
      });
      setNewName(''); setCreatingFor(null);
      await loadFolders();
      if (forVal && forVal !== 'personal') setExpandedWs(e => ({ ...e, [forVal]: true }));
      toast.success('Folder created');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create folder'); }
  };

  const removeFolder = async (id) => {
    try { await foldersApi.delete(id); if (activeFolder === id) setActiveFolder('all'); await loadFolders(); await load(); toast.success('Folder deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolderTarget || folderConfirmName.trim() !== deleteFolderTarget.name) return;
    setDeletingFolder(true);
    try {
      await removeFolder(deleteFolderTarget.id);
      setDeleteFolderTarget(null); setFolderConfirmName('');
    } finally { setDeletingFolder(false); }
  };

  const moveCard = async (cardId, folderId, teamId) => {
    try {
      await actionCardsApi.move(cardId, folderId, teamId);
      toast.success(teamId ? 'Moved to workspace' : folderId ? 'Moved to folder' : 'Moved to your default workspace');
      await load(); await loadFolders();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to move card'); }
  };

  // Drop-zone props for a folder/target. `key` identifies the highlighted target.
  const dropProps = (key, folderId, teamId) => ({
    onDragOver: e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropTarget !== key) setDropTarget(key); },
    onDragLeave: e => { if (e.currentTarget === e.target) setDropTarget(null); },
    onDrop: e => {
      e.preventDefault();
      const cardId = e.dataTransfer.getData('text/cardId');
      setDropTarget(null); setDragging(false);
      if (cardId) moveCard(cardId, folderId, teamId);
    },
    style: dropTarget === key ? { outline: '2px dashed #6994c6', outlineOffset: '-2px', background: 'rgba(105,148,198,0.10)', borderRadius: 8 } : undefined,
  });

  const FolderItem = ({ f }) => {
    const subs = subOf(f.id);
    const isOpen = expanded[f.id];
    return (
      <div>
        <div className={`fr-item${activeFolder === f.id ? ' active' : ''}`} data-testid={`folder-item-${f.id}`} {...dropProps(`f-${f.id}`, f.id, null)}>
          {subs.length > 0 ? (
            <button onClick={() => setExpanded(e => ({ ...e, [f.id]: !e[f.id] }))} className="fr-caret">
              <ChevronRight size={12} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
            </button>
          ) : <span className="fr-caret" />}
          <button className="fr-label" onClick={() => setActiveFolder(f.id)}>
            {f.emoji ? <span style={{ fontSize: 13, lineHeight: 1 }}>{f.emoji}</span> : <Folder size={13} style={{ color: folderColorOf(f) }} />}{f.name}
            <span className="fr-count">{f.card_count || ''}</span>
          </button>
          <button className="fr-del" onClick={() => setDeleteFolderTarget(f)} title="Delete folder" data-testid={`folder-delete-${f.id}`}><Trash2 size={12} /></button>
        </div>
        {isOpen && subs.map(s => (
          <div key={s.id} className={`fr-item fr-sub${activeFolder === s.id ? ' active' : ''}`} data-testid={`folder-item-${s.id}`} {...dropProps(`f-${s.id}`, s.id, null)}>
            <span className="fr-caret" />
            <button className="fr-label" onClick={() => setActiveFolder(s.id)}>{s.emoji ? <span style={{ fontSize: 12, lineHeight: 1 }}>{s.emoji}</span> : <Folder size={12} style={{ color: folderColorOf(s) }} />}{s.name}<span className="fr-count">{s.card_count || ''}</span></button>
            <button className="fr-del" onClick={() => setDeleteFolderTarget(s)} title="Delete" data-testid={`folder-delete-${s.id}`}><Trash2 size={11} /></button>
          </div>
        ))}
      </div>
    );
  };

  const renderCreateInput = () => (
    <div className="fr-new">
      <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setCreatingFor(null); setNewName(''); } }}
        placeholder="Folder name" className="fr-new-input" data-testid="new-folder-input" />
      <div className="fr-new-row">
        <button className="fr-new-go" onClick={createFolder} data-testid="create-folder-confirm"><Check size={13} /></button>
        <button className="fr-new-go" onClick={() => { setCreatingFor(null); setNewName(''); }}><X size={13} /></button>
      </div>
    </div>
  );

  const renderWorkspaceNode = (t) => {
    const tf = topTeam.filter(f => f.team_id === t.id);
    const isOpen = expandedWs[t.id];
    return (
      <div key={t.id} data-testid={`ws-node-${t.id}`}>
        <div className="fr-item" {...dropProps(`ws-${t.id}`, null, t.id)}>
          <button className="fr-caret" onClick={() => setExpandedWs(e => ({ ...e, [t.id]: !e[t.id] }))} data-testid={`ws-toggle-${t.id}`}>
            <ChevronRight size={12} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
          </button>
          <button className="fr-label" onClick={() => setExpandedWs(e => ({ ...e, [t.id]: !e[t.id] }))}>
            <Users size={13} style={{ color: '#6994c6' }} />{t.name}
            {t.pinned && <Pin size={10} fill="currentColor" style={{ color: '#c79a3f' }} />}
          </button>
        </div>
        {isOpen && (
          <div style={{ paddingLeft: 12 }}>
            {tf.map(f => <FolderItem key={f.id} f={f} />)}
            {creatingFor === t.id ? renderCreateInput() : (
              <button className="fr-add fr-sub" onClick={() => { setNewName(''); setCreatingFor(t.id); }} data-testid={`ws-new-folder-${t.id}`}>
                <FolderPlus size={13} />New folder
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const pinnedWs = teams.filter(t => t.pinned);
  const unpinnedWs = teams.filter(t => !t.pinned);
  const wsSlots = Math.max(0, 10 - pinnedWs.length);
  const visibleUnpinnedWs = showAllWsFolders ? unpinnedWs : unpinnedWs.slice(0, wsSlots);
  const displayWs = [...pinnedWs, ...visibleUnpinnedWs];
  const hiddenWsCount = unpinnedWs.length - visibleUnpinnedWs.length;

  // ── List (client filters + workspace grouping) ────────────────────
  const activeFilters = (openTasksOnly ? 1 : 0) + (orgTeamFilter ? 1 : 0) + (wsFilter !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0);
  const statusPass = (c) => {
    if (statusFilter === 'all') return true;
    const counts = c.task_status_counts || {};
    const total = (counts.not_started || 0) + (counts.in_progress || 0) + (counts.done || 0);
    if (statusFilter === 'done') return total > 0 && (counts.done || 0) === total;
    return (counts[statusFilter] || 0) > 0;
  };
  const filtered = cards.filter(c => (openTasksOnly ? (c.task_ids?.length || 0) > 0 : true)
    && (wsFilter === 'all' || c.team_id === wsFilter)
    && statusPass(c));
  const sorted = sortBy === 'tasks'
    ? [...filtered].sort((a, b) => (b.task_ids?.length || 0) - (a.task_ids?.length || 0))
    : filtered;

  const groups = [];
  const orderedTeams = [...teams].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  orderedTeams.forEach(t => {
    const tc = sorted.filter(c => c.team_id === t.id);
    if (tc.length) groups.push({ key: t.id, label: t.name, team: t, cards: tc });
  });
  const hasAny = groups.length > 0;
  const legendVisibleFolderIds = new Set(groups.flatMap(g => g.cards.map(c => c.folder_id).filter(Boolean)));
  const legendFolders = folders.filter(f => (f.color || f.emoji) && legendVisibleFolderIds.has(f.id));

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await actionCardsApi.delete(deleteTarget.id);
      toast.success('Action Card deleted');
      setDeleteTarget(null);
      await load(); await loadFolders();
    } catch { toast.error('Failed to delete card'); }
    finally { setDeleting(false); }
  };

  const clearFilters = () => { setOpenTasksOnly(false); setOrgTeamFilter(''); setWsFilter('all'); setStatusFilter('all'); setOpenMenu(null); };
  const isSel = (card, folderId, teamId) => {
    if (teamId) return card.team_id === teamId && !card.folder_id;
    if (folderId) return card.folder_id === folderId;
    return !card.team_id && !card.folder_id;
  };

  const CardRow = (card) => {
    const meta = typeMetaOf(card.source);
    const Icon = meta.Icon;
    const taskCount = card.task_ids?.length || 0;
    const emailCount = card.emails_sent?.length || 0;
    const decCount = card.decisions?.length || 0;
    const tags = card.tags || [];
    const menuOpen = rowMenuId === card.id;
    const rowFolder = card.folder_id ? folders.find(f => f.id === card.folder_id) : null;
    const stripColor = rowFolder ? folderColorOf(rowFolder) : null;
    return (
      <div
        key={card.id}
        className={`row${menuOpen ? ' menu-open' : ''}`}
        data-testid="action-card-row"
        draggable
        onDragStart={e => { e.dataTransfer.setData('text/cardId', card.id); e.dataTransfer.effectAllowed = 'move'; setDragging(true); }}
        onDragEnd={() => { setDragging(false); setDropTarget(null); }}
        onClick={() => navigate(`/action-cards/${card.id}`)}
      >
        {stripColor && <span className="row-strip" style={{ background: stripColor }} data-testid={`card-folder-strip-${card.id}`} title={rowFolder?.name} />}
        <div className="r-name">
          <div className={`type-ic ${meta.cls}`} data-tip={meta.tip}><Icon size={13} /></div>
          {rowFolder?.emoji && <span className="fldr-badge" title={rowFolder.name}>{rowFolder.emoji}</span>}
          <span className="tt" data-testid={`card-title-${card.id}`}>{card.title || card.contact_name || 'Untitled'}</span>
          {decCount > 0 && (
            <span className="decct" data-tip={`${decCount} decision${decCount === 1 ? '' : 's'}`}><Sparkles size={12} />{decCount}</span>
          )}
          {emailCount > 0 && (
            <span className="decct" data-tip={`${emailCount} email${emailCount === 1 ? '' : 's'} sent`}><Mail size={12} />{emailCount}</span>
          )}
          {tags.length > 0 && (
            <span className="tags">{tags.slice(0, 3).map(t => `#${t}`).join(' ')}</span>
          )}
        </div>
        <div className="r-tk" data-testid={`card-tasks-${card.id}`}>
          {taskCount > 0 ? <b>{taskCount}</b> : <span className="none">—</span>}
        </div>
        <div className="r-when">{relTime(card.created_at)}</div>
        <div className="r-dots dd" data-menuroot onClick={e => e.stopPropagation()}>
          <button
            className="icbtn rowdots"
            data-testid={`card-menu-button-${card.id}`}
            onClick={() => { setRowMenuId(menuOpen ? null : card.id); setOpenMenu(null); }}
            title="Card actions"
          >
            <MoreHorizontal size={16} />
          </button>
          <div className={`menu dd-menu${menuOpen ? ' open' : ''}`}>
            <button className="mi" data-testid={`card-open-${card.id}`}
              onClick={() => { setRowMenuId(null); navigate(`/action-cards/${card.id}`); }}>
              <ExternalLink />Open card
            </button>
            {!(card.team_id && teams.find(t => t.id === card.team_id)?.my_role === 'viewer') && (
              <>
                <button className="mi" data-testid={`card-move-${card.id}`}
                  onClick={() => { setRowMenuId(null); setMoveTarget(card); }}>
                  <FolderInput />Move to…
                </button>
                <div className="msep" />
                <button className="mi danger" data-testid={`card-delete-${card.id}`}
                  onClick={() => { setRowMenuId(null); setDeleteTarget(card); }}>
                  <Trash2 />Delete card
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pagewrap">
      {/* Header */}
      <div className="page-head">
        <div>
          <h1>Action Cards</h1>
          <p className="sub">{total} conversation{total !== 1 ? 's' : ''} — captured, poured, and ready to finish</p>
        </div>
        <button className="btn-capture-top" onClick={openModal} data-testid="action-cards-new-button">
          <Plus size={16} />Capture
        </button>
      </div>

      {/* Toolbar — full width, above the folder rail */}
      <div className="toolbar" data-testid="cards-toolbar">
        <div className="tool-search" style={{ maxWidth: 'none' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          <TagSearchInput
            testId="action-cards-search"
            value={search}
            onChange={setSearch}
            tags={[...new Set(cards.flatMap(c => c.tags || []))].sort((a, b) => a.localeCompare(b))}
            placeholder="Search conversations — use #tag to search by tag"
            inputClassName="w-full border-0 bg-transparent outline-none"
          />
        </div>
        <div className="tool-right">
          {/* Workspace filter */}
          <div className="dd" data-menuroot>
            <button className={`tbtn${wsFilter !== 'all' ? ' on' : ''}`} data-testid="cards-ws-filter-btn"
              onClick={() => setOpenMenu(m => m === 'ws' ? null : 'ws')}>
              <Users size={15} />{wsFilter !== 'all' ? (teams.find(t => t.id === wsFilter)?.name || 'Workspace') : 'Workspace'}
            </button>
            <div className={`menu dd-menu${openMenu === 'ws' ? ' open' : ''}`}>
              <button className={`mi${wsFilter === 'all' ? ' on' : ''}`} data-testid="cards-ws-filter-all"
                onClick={() => { setWsFilter('all'); setOpenMenu(null); }}>All workspaces<span className="mcheck"><Check size={14} /></span></button>
              <div className="msep" />
              {teams.filter(t => t.is_member).map(t => (
                <button key={t.id} className={`mi${wsFilter === t.id ? ' on' : ''}`} data-testid={`cards-ws-filter-${t.id}`}
                  onClick={() => { setWsFilter(wsFilter === t.id ? 'all' : t.id); setOpenMenu(null); }}>
                  <Users size={15} />{t.name}<span className="mcheck"><Check size={14} /></span>
                </button>
              ))}
            </div>
          </div>

          {/* Status filter (canonical task statuses) */}
          <div className="dd" data-menuroot>
            <button className={`tbtn${statusFilter !== 'all' ? ' on' : ''}`} data-testid="cards-status-filter-btn"
              onClick={() => setOpenMenu(m => m === 'status' ? null : 'status')}>
              <ListChecks size={15} />{statusFilter === 'not_started' ? 'Not started' : statusFilter === 'in_progress' ? 'In progress' : statusFilter === 'done' ? 'Completed' : 'Status'}
            </button>
            <div className={`menu dd-menu${openMenu === 'status' ? ' open' : ''}`}>
              <div className="mlab">Cards with tasks…</div>
              {[['not_started', 'Not started'], ['in_progress', 'In progress'], ['done', 'Completed']].map(([val, label]) => (
                <button key={val} className={`mi${statusFilter === val ? ' on' : ''}`} data-testid={`cards-status-filter-${val}`}
                  onClick={() => { setStatusFilter(statusFilter === val ? 'all' : val); setOpenMenu(null); }}>
                  {label}<span className="mcheck"><Check size={14} /></span>
                </button>
              ))}
              <div className="msep" />
              <button className={`mi${statusFilter === 'all' ? ' on' : ''}`} data-testid="cards-status-filter-all"
                onClick={() => { setStatusFilter('all'); setOpenMenu(null); }}>Any status<span className="mcheck"><Check size={14} /></span></button>
            </div>
          </div>

          {/* Filter */}
          <div className="dd" data-menuroot>
            <button className={`tbtn${activeFilters > 0 ? ' on' : ''}`} data-testid="cards-filter-btn"
              onClick={() => setOpenMenu(m => m === 'filter' ? null : 'filter')}>
              <SlidersHorizontal size={15} />Filter
              {activeFilters > 0 && <span className="fct">{activeFilters}</span>}
            </button>
            <div className={`menu dd-menu${openMenu === 'filter' ? ' open' : ''}`}>
              <div className="mlab">Quick filters</div>
              <button className={`mi${openTasksOnly ? ' on' : ''}`} data-testid="cards-filter-open-tasks"
                onClick={() => setOpenTasksOnly(v => !v)}>
                <Zap size={15} />Has open tasks<span className="mcheck"><Check size={14} /></span>
              </button>
              {orgTeams.length > 0 && <>
                <div className="msep" />
                <div className="mlab">Organization team</div>
                {orgTeams.map(t => (
                  <button key={t.id} className={`mi${orgTeamFilter === t.id ? ' on' : ''}`} data-testid={`cards-filter-org-team-${t.id}`}
                    onClick={() => setOrgTeamFilter(orgTeamFilter === t.id ? '' : t.id)}>
                    <Building2 size={15} />{t.name}<span className="mcheck"><Check size={14} /></span>
                  </button>
                ))}
              </>}
              <div className="msep" />
              <button className="mi" data-testid="cards-filter-clear" onClick={clearFilters}>
                <X size={14} />Clear filters
              </button>
            </div>
          </div>

          {/* Sort */}
          <div className="dd" data-menuroot>
            <button className={`tbtn${sortBy !== 'new' ? ' on' : ''}`} data-testid="cards-sort-btn"
              onClick={() => setOpenMenu(m => m === 'sort' ? null : 'sort')}>
              <ArrowUpDown size={15} />Sort: {sortBy === 'tasks' ? 'Open tasks' : 'Newest'}
            </button>
            <div className={`menu dd-menu${openMenu === 'sort' ? ' open' : ''}`}>
              <div className="mlab">Sort cards by</div>
              <button className={`mi${sortBy === 'new' ? ' on' : ''}`} data-testid="cards-sort-new"
                onClick={() => { setSortBy('new'); setOpenMenu(null); }}>Newest<span className="mcheck"><Check size={14} /></span></button>
              <button className={`mi${sortBy === 'tasks' ? ' on' : ''}`} data-testid="cards-sort-tasks"
                onClick={() => { setSortBy('tasks'); setOpenMenu(null); }}>Most open tasks<span className="mcheck"><Check size={14} /></span></button>
            </div>
          </div>

          {/* Customize columns */}
          <div className="dd" data-menuroot>
            <button className="tbtn" data-testid="cards-customize-btn"
              onClick={() => setOpenMenu(m => m === 'cust' ? null : 'cust')}>
              <Settings2 size={15} />Customize
            </button>
            <div className={`menu dd-menu${openMenu === 'cust' ? ' open' : ''}`}>
              <div className="mlab">Show columns</div>
              {[['tk', 'Tasks'], ['when', 'Captured']].map(([k, label]) => (
                <button key={k} className={`mi${!hiddenCols[k] ? ' on' : ''}`} data-testid={`cards-col-${k}`}
                  onClick={() => setHiddenCols(c => ({ ...c, [k]: !c[k] }))}>{label}<span className="mcheck"><Check size={14} /></span></button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="ac-layout">
        {/* ── Folder rail ── */}
        <aside className="folder-rail" data-testid="folder-rail">
          {dragging && (
            <div className="text-[11px] font-medium text-center px-2 py-1.5 mb-1 rounded-lg" style={{ background: 'rgba(105,148,198,0.12)', color: '#4f74a0' }} data-testid="drag-hint">
              Drop onto a folder to move
            </div>
          )}
          <CardsSideNav onNavigate={(id) => {
            if (id === 'personal') setActiveFolder('personal');
            else if (id === 'team') setActiveFolder('team');
            else if (id === 'all') setActiveFolder('all');
          }} />

          <div className="fr-divider" />
          <div className="fr-sect">Workspaces</div>
          {teams.length === 0 ? (
            <p className="text-[11px] text-muted-foreground px-2 py-1.5">No workspaces yet</p>
          ) : (
            <>
              {displayWs.map(t => renderWorkspaceNode(t))}
              {(hiddenWsCount > 0 || showAllWsFolders) && (
                <button className="fr-add" onClick={() => setShowAllWsFolders(v => !v)} data-testid="ws-view-more">
                  <ChevronDown size={13} />{showAllWsFolders ? 'Show less' : `View more (${hiddenWsCount})`}
                </button>
              )}
            </>
          )}
        </aside>

        {/* ── List column ── */}
        <div>
          {/* List */}
          <div className="list-wrap" data-testid="action-cards-list">
            {loading ? (
              <div>{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-[40px] w-full rounded-none" />)}</div>
            ) : !hasAny ? (
              <div className="ac-empty" data-testid="action-cards-empty">
                <div className="eic"><Zap size={21} fill="currentColor" /></div>
                <div className="et">No cards here</div>
                <div className="es">
                  {activeFilters > 0 || search ? 'Try clearing the filters'
                    : activeFolder !== 'all' ? 'Move cards into this folder, or pick another view'
                    : 'Capture a conversation to get started'}
                </div>
              </div>
            ) : (
              <>
                {legendFolders.length > 0 && (
                  <div className="ac-legend" data-testid="action-cards-color-legend">
                    <span className="ac-legend-lbl">Folders</span>
                    {legendFolders.map(f => (
                      <button key={f.id} className={`ac-legend-item${activeFolder === f.id ? ' on' : ''}`} data-testid={`action-cards-legend-${f.id}`}
                        onClick={() => setActiveFolder(activeFolder === f.id ? 'all' : f.id)}
                        title={activeFolder === f.id ? 'Clear filter' : `Show only ${f.name}`}>
                        {f.emoji ? <span className="em">{f.emoji}</span> : <span className="dot" style={{ background: folderColorOf(f) }} />}
                        {f.name}
                      </button>
                    ))}
                  </div>
                )}
                <div className="cols">
                  <div className="c-name">Conversation</div>
                  <div className="c-tk">Tasks</div>
                  <div className="c-when">Captured</div>
                  <div className="c-dots" />
                </div>
                {groups.map(g => {
                  const isClosed = !!collapsed[g.key];
                  return (
                    <div key={g.key} className={`section${isClosed ? ' closed' : ''}`} data-testid={`cards-section-${g.key}`}>
                      <div className="sec-head">
                        <button className="sec-caret" onClick={() => setCollapsed(c => ({ ...c, [g.key]: !c[g.key] }))}
                          data-testid={`cards-section-toggle-${g.key}`} aria-label={`Toggle ${g.label}`}>
                          <ChevronDown />
                        </button>
                        {g.team && <span className="sec-ws-ic"><Users /></span>}
                        <span className="sec-name">{g.label}</span>
                        <span className="sec-count">{g.cards.length}</span>
                      </div>
                      <div className="sec-body">
                        {g.cards.map(CardRow)}
                      </div>
                    </div>
                  );
                })}
                <div className="newcap-row" onClick={openModal} data-testid="cards-new-capture-row">
                  <Plus />New capture
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Move modal */}
      <div className={`modal-bd${moveTarget ? ' open' : ''}`} onClick={() => setMoveTarget(null)} data-testid="card-move-modal-backdrop">
        {moveTarget && (
          <div className="modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
            <h3>Move card
              <button className="icbtn" onClick={() => setMoveTarget(null)} aria-label="Close"><X size={16} /></button>
            </h3>
            <p className="mp">Choose where <b>{moveTarget.title || moveTarget.contact_name}</b> should live.</p>
            <div className="move-scroll">
              {teams.filter(t => t.is_member && t.my_role !== 'viewer').map(t => (
                <div key={t.id}>
                  <div className="mlab" style={{ paddingLeft: 2 }}>{t.name}</div>
                  <div className="radio" style={isSel(moveTarget, null, t.id) ? { borderColor: 'var(--blue)', background: 'var(--tint)' } : undefined}
                    onClick={() => { moveCard(moveTarget.id, null, t.id); setMoveTarget(null); }} data-testid={`move-to-workspace-${t.id}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Users size={15} style={{ color: 'var(--blue-ink)' }} /><span className="rt">Workspace (no folder)</span></div>
                    {isSel(moveTarget, null, t.id) && <Check size={16} style={{ marginLeft: 'auto', color: 'var(--blue-d)' }} />}
                  </div>
                  {teamFoldersOf(t.id).map(f => (
                    <div key={f.id} className="radio" style={isSel(moveTarget, f.id, null) ? { borderColor: 'var(--blue)', background: 'var(--tint)' } : undefined}
                      onClick={() => { moveCard(moveTarget.id, f.id, null); setMoveTarget(null); }} data-testid={`move-to-team-folder-${f.id}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Folder size={15} style={{ color: 'var(--blue-ink)' }} /><span className="rt">{f.parent_id ? '— ' : ''}{f.name}</span></div>
                      {isSel(moveTarget, f.id, null) && <Check size={16} style={{ marginLeft: 'auto', color: 'var(--blue-d)' }} />}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete modal */}
      <div className={`modal-bd${deleteTarget ? ' open' : ''}`} onClick={() => !deleting && setDeleteTarget(null)} data-testid="card-delete-modal-backdrop">
        {deleteTarget && (
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Delete this Action Card?
              <button className="icbtn" onClick={() => setDeleteTarget(null)} aria-label="Close"><X size={16} /></button>
            </h3>
            <p className="mp">
              <b>{deleteTarget.title || deleteTarget.contact_name}</b>{(deleteTarget.task_ids?.length || 0) > 0 ? ` holds ${deleteTarget.task_ids.length} task${deleteTarget.task_ids.length === 1 ? '' : 's'}. Tasks already poured stay in Tasks; ` : '. '}
              the card and its transcript are removed. This can't be undone.
            </p>
            <div className="m-foot">
              <button className="btn-plain" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button className="btn-danger" onClick={doDelete} disabled={deleting} data-testid="card-delete-confirm">
                {deleting ? 'Deleting…' : 'Delete card'}
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Delete folder modal — type the folder name to confirm */}
      <div className={`modal-bd${deleteFolderTarget ? ' open' : ''}`} onClick={() => { if (!deletingFolder) { setDeleteFolderTarget(null); setFolderConfirmName(''); } }} data-testid="folder-delete-modal-backdrop">
        {deleteFolderTarget && (
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Delete folder?
              <button className="icbtn" onClick={() => { setDeleteFolderTarget(null); setFolderConfirmName(''); }} aria-label="Close"><X size={16} /></button>
            </h3>
            <p className="mp">
              This deletes the folder <b>{deleteFolderTarget.name}</b>. Cards inside it are not deleted — they move back to the workspace root. This can't be undone.
            </p>
            <p className="mp" style={{ marginBottom: 6 }}>Type <b>{deleteFolderTarget.name}</b> to confirm:</p>
            <input
              autoFocus
              value={folderConfirmName}
              onChange={e => setFolderConfirmName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmDeleteFolder(); }}
              placeholder={deleteFolderTarget.name}
              data-testid="folder-delete-confirm-input"
              style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', fontSize: 14, outline: 'none', color: 'var(--ink)', background: 'var(--paper)' }}
            />
            <div className="m-foot">
              <button className="btn-plain" onClick={() => { setDeleteFolderTarget(null); setFolderConfirmName(''); }} disabled={deletingFolder}>Cancel</button>
              <button className="btn-danger" onClick={confirmDeleteFolder}
                disabled={deletingFolder || folderConfirmName.trim() !== deleteFolderTarget.name}
                data-testid="folder-delete-confirm">
                {deletingFolder ? 'Deleting…' : 'Delete folder'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
